/**
 * Choose a MediaRecorder mime type that matches:
 * - API `/v1/attachment/sign-url` allowlist (use uploadMime without codec params)
 * - Native apps: prefer AAC in MP4 (`.m4a` / `audio/mp4`) like iOS / RN expo-audio HIGH_QUALITY
 *
 * Order: MP4 → Ogg Opus → WebM Opus → WebM (Chrome fallback).
 * Backend should allow `audio/webm` (and optionally `audio/webm;codecs=opus`) for maximum web coverage;
 * web still prefers mp4/ogg when the browser supports them.
 */

const CANDIDATES = [
  {
    recorderTypes: ['audio/mp4', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4;codecs=aac'],
    uploadMime: 'audio/mp4',
    ext: 'm4a',
  },
  {
    recorderTypes: ['audio/ogg;codecs=opus', 'audio/ogg'],
    uploadMime: 'audio/ogg',
    ext: 'ogg',
  },
  {
    recorderTypes: ['audio/webm;codecs=opus'],
    uploadMime: 'audio/webm',
    ext: 'webm',
  },
  {
    recorderTypes: ['audio/webm'],
    uploadMime: 'audio/webm',
    ext: 'webm',
  },
];

/**
 * @returns {{ recorderMime: string, uploadMime: string, ext: string } | null}
 */
export function pickVoiceRecordingFormat() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return null;
  }
  for (const c of CANDIDATES) {
    for (const t of c.recorderTypes) {
      if (MediaRecorder.isTypeSupported(t)) {
        return { recorderMime: t, uploadMime: c.uploadMime, ext: c.ext };
      }
    }
  }
  return null;
}

/**
 * Infer upload mime + ext from MediaRecorder instance, blob, or raw mime string.
 * @param {MediaRecorder | Blob | { mimeType?: string } | string} source
 */
export function inferVoiceUploadFormatFromRecorder(source) {
  const raw =
    typeof source === 'string'
      ? source
      : source instanceof Blob
        ? source.type
        : source?.mimeType || '';
  const base = raw.split(';')[0].trim().toLowerCase() || 'audio/webm';
  if (base === 'audio/mp4' || base === 'audio/m4a' || base === 'audio/x-m4a') {
    return { uploadMime: 'audio/mp4', ext: 'm4a' };
  }
  if (base === 'audio/ogg') {
    return { uploadMime: 'audio/ogg', ext: 'ogg' };
  }
  if (base === 'audio/webm') {
    return { uploadMime: 'audio/webm', ext: 'webm' };
  }
  return { uploadMime: base, ext: 'm4a' };
}
